import React from "react";
import useAccess from "../hooks/useAccess.jsx";
import { UserUiPreferencesProvider } from "../hooks/useUserUiPreferences.js";
import { resetDispatchProductTagsSession } from "../hooks/useDispatchDisplayPreferences.js";
import { clearPreferencesTarget, readPreferencesTarget } from "../utils/preferencesDelegation.js";
import { useToast } from "./ResultsPrimitives.jsx";
import { initializeUserUiPreferenceStorage } from "../lib/userUiPreferenceStorage.js";
import { resolveUserUiPreferencesUserId } from "../utils/userUiPreferences.js";
import { mirrorConfirmedUserUiPreferencesToLegacy, readLegacyUserUiPreferencesPatch } from "../utils/legacyUserUiPreferences.js";
import { endPreferencesTarget } from "../utils/preferencesDelegation.js";
import { startUserUiPreferencesSync } from "../lib/userUiPreferencesSync.js";
import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";

export default function ApplicationUserUiPreferencesProvider({ children }) {
  const { session, isReady } = useAccess();
  const { showToast } = useToast();
  React.useEffect(() => {
    if (isReady && !session) {
      resetDispatchProductTagsSession();
      clearPreferencesTarget();
    }
  }, [isReady, session]);
  // Admin qui règle pour un autre : le cache local de ses préférences reste séparé de celui de l'Admin.
  const preferencesUserId = session ? readPreferencesTarget()?.userId : undefined;
  const actorId = resolveUserUiPreferencesUserId(session);
  const storageUserId = preferencesUserId || actorId;
  const delegated = Boolean(preferencesUserId && preferencesUserId !== actorId);
  // Ces lecteurs gardent leur identité même si une réponse arrive pendant « Terminer ».
  const readOwnLegacy = React.useCallback((...args) => delegated ? {} : readLegacyUserUiPreferencesPatch(...args), [delegated]);
  const mirrorOwnLegacy = React.useCallback((...args) => delegated ? false : mirrorConfirmedUserUiPreferencesToLegacy(...args), [delegated]);
  const [storageState, setStorageState] = React.useState({ userId: "", error: null });
  React.useEffect(() => {
    if (!isReady || !storageUserId) return undefined;
    const controller = new AbortController();
    let dispose;
    setStorageState({ userId: "", error: null });
    void initializeUserUiPreferenceStorage({
      userId: storageUserId,
      delegated,
      signal: controller.signal,
      onError: (error) => showToast({ title: "Préférence non enregistrée", message: `${error.message} Le dernier réglage confirmé est conservé.`, type: "error" }),
    }).then((cleanup) => {
      if (controller.signal.aborted) { cleanup(); return; }
      const stopSync = startUserUiPreferencesSync({ userId: storageUserId, fetchImpl: fetchWithSupabaseAuth });
      dispose = () => { stopSync(); cleanup(); };
      if (!controller.signal.aborted) setStorageState({ userId: storageUserId, error: null });
    }).catch((error) => {
      if (!controller.signal.aborted) setStorageState({ userId: storageUserId, error });
    });
    return () => { controller.abort(); dispose?.(); };
  }, [delegated, isReady, showToast, storageUserId]);
  const handlePreferenceError = React.useCallback((error, meta) => {
    if (meta?.operation !== "save") {
      return;
    }

    const errorMessage = error instanceof Error && error.message.trim()
      ? error.message.trim()
      : "La préférence n’a pas pu être enregistrée.";
    showToast({
      title: "Préférence non enregistrée",
      message: `${errorMessage} La dernière valeur synchronisée a été restaurée.`,
      type: "error",
      duration: 7000,
    });
  }, [showToast]);

  return (
    <UserUiPreferencesProvider session={session} userId={preferencesUserId} onError={handlePreferenceError}
      readLegacy={readOwnLegacy} mirrorConfirmed={mirrorOwnLegacy} keepOpenPageDatesLocal={delegated}>
      {storageUserId && storageState.userId !== storageUserId ? (
        <div role="status" className="sr-only">Chargement des préférences du compte…</div>
      ) : storageUserId && storageState.error ? (
        <div role="alert" className="m-4 rounded-[4px] border border-slate-300 bg-white p-4 text-sm text-slate-700">
          <p>{storageState.error.message}</p>
          <button type="button" onClick={() => window.location.reload()} className="mr-2 rounded-[4px] border border-slate-300 px-3 py-1 hover:bg-slate-50">Recharger</button>
          {preferencesUserId ? <button type="button" onClick={() => endPreferencesTarget()} className="rounded-[4px] border border-slate-300 px-3 py-1 hover:bg-slate-50">Terminer</button> : null}
        </div>
      ) : children}
    </UserUiPreferencesProvider>
  );
}
